import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  GlassCard,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import {
  SEED_PLUGIN_SKILL_BODIES,
  SEED_PLUGIN_SKILLS,
} from '@/versions/v1/shared/settings-seed';

export function PluginSkillDetailScene() {
  const { pluginId = '', name = '' } = useParams<{
    pluginId: string;
    name: string;
  }>();

  const skill = useMemo(
    () =>
      SEED_PLUGIN_SKILLS.find(
        (entry) => entry.pluginId === pluginId && entry.name === name,
      ),
    [pluginId, name],
  );

  const bodySeed = useMemo(
    () =>
      SEED_PLUGIN_SKILL_BODIES.find(
        (entry) => entry.pluginId === pluginId && entry.name === name,
      ),
    [pluginId, name],
  );

  if (!skill) {
    return (
      <SurfacePageLayout width="narrow">
        <SurfacePageBody className="space-y-4">
          <p className="text-sm text-muted">Plugin skill not found.</p>
          <Link
            to="/settings/skills"
            className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to skills
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="PluginSkillDetailScene">
        <SurfacePageBody className="space-y-4" as="div">
          <Link
            to="/settings/skills"
            className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to skills
          </Link>

          <GlassCard className="flex flex-col gap-3 p-4 shadow-sm">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted">
                Name
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {skill.name}
                </span>
                <Pill>{`plugin:${skill.pluginName}`}</Pill>
              </div>
              <p className="text-xs text-muted">
                Managed by plugin <code>{skill.pluginName}</code>. Read-only — to
                customize, create your own personal skill with a different name.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted">
                Description
              </span>
              <p className="text-sm text-zinc-700 dark:text-zinc-300">
                {skill.description || (
                  <span className="italic text-zinc-400 dark:text-zinc-600">
                    No description
                  </span>
                )}
              </p>
            </div>
          </GlassCard>

          {bodySeed?.blockedReason ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
              {bodySeed.blockedReason}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted">
              Body (read-only)
            </span>
            <GlassCard className="max-h-[60vh] overflow-y-auto p-4">
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-900 dark:text-zinc-100">
                {bodySeed?.body ?? 'No body content seeded for this plugin skill.'}
              </pre>
            </GlassCard>
          </div>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
